import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private readonly MAINTENANCE_KEY = 'maintenance_auth';
  private readonly API_KEY_STORAGE_KEY = 'xray_api_key';
private readonly AUTH_DURATION = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
  private readonly apiBaseUrl = environment.xrayUrl;

  private authStatusSubject: BehaviorSubject<boolean>;
  public authStatus$: Observable<boolean>;

  constructor(private http: HttpClient) {
    // Always initialize subject after all properties
    this.authStatusSubject = new BehaviorSubject<boolean>(false);
    this.authStatus$ = this.authStatusSubject.asObservable();
    // Set initial value
    this.checkAuthStatus();
  }
  /**
   * Validate API key via backend endpoint
   */
  validateApiKey(apiKey: string): Observable<boolean> {
    const headers = {
        'Content-Type': 'application/json',
        'x-api-key': apiKey
    };
    return this.http.get<any>(`${this.apiBaseUrl}api/v1/validate-api-key`, { headers }).pipe(
      map(res => {
        const isValid = !!(res && res.success);

        if (isValid) {
          // Persist API key and mark user as authenticated
          this.setApiKey(apiKey);
          this.setAuthenticated();
        }

        return isValid;
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Check if user is currently authenticated for maintenance mode
   */
  isAuthenticated(): boolean {
    try {
      const authData = sessionStorage.getItem(this.MAINTENANCE_KEY);
      if (!authData) {
        return false;
      }
      const { timestamp } = JSON.parse(authData);
      const now = Date.now();
      const timeDiff = now - timestamp;
      // Check if auth has expired
      if (timeDiff > this.AUTH_DURATION) {
        this.clearAuthentication();
        // Optionally, trigger redirect or modal logic here if needed
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error checking maintenance auth:', error);
      this.clearAuthentication();
      return false;
    }
  }
  /**
   * Force check auth status and emit update
   */
  checkAuthStatus(): void {
    const isAuth = this.isAuthenticated();
    if (this.authStatusSubject) {
      this.authStatusSubject.next(isAuth);
    }
  }

  /**
   * Set authentication flag with expiry
   */
  setAuthenticated(): void {
    try {
      const authData = {
        timestamp: Date.now(),
        authenticated: true
      };

      sessionStorage.setItem(this.MAINTENANCE_KEY, JSON.stringify(authData));
      this.authStatusSubject.next(true);
    } catch (error) {
      console.error('Error setting maintenance auth:', error);
    }
  }

  /**
   * Clear authentication
   */
  clearAuthentication(): void {
      sessionStorage.removeItem(this.MAINTENANCE_KEY);
      this.clearApiKey();
      this.authStatusSubject.next(false);
  }

  /**
   * Check if we should show the modal (user not authenticated)
   */
  shouldShowModal(): boolean {
    return !this.isAuthenticated();
  }

  /**
   * Store API key used by interceptor
   */
  private setApiKey(apiKey: string): void {
    try {
      sessionStorage.setItem(this.API_KEY_STORAGE_KEY, apiKey);
    } catch (error) {
      console.error('Error saving API key:', error);
    }
  }

  /**
   * Retrieve API key for outgoing requests
   */
  getApiKey(): string | null {
    try {
      return sessionStorage.getItem(this.API_KEY_STORAGE_KEY);
    } catch (error) {
      console.error('Error reading API key:', error);
      return null;
    }
  }

  /**
   * Remove stored API key
   */
  private clearApiKey(): void {
    try {
      sessionStorage.removeItem(this.API_KEY_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing API key:', error);
    }
  }
}
